import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import { defineStore } from 'pinia';

import type { Product } from '~/stores/product/types';

import { ProductSort, SortDirection } from '~/stores/category/types';
import { ApiMethod, handleApiError } from '~/utils/apiClient';

const STORE_ID = 'product';

interface Actions {
  fetchProduct: (slug: string, locale: Locale) => Promise<Product | null>;
  searchProducts: (query: string, locale: Locale, quickSearch?: boolean) => Promise<Product[] | null>;
  selectVariantAttribute: (label: string, value: string) => void;
  setCurrentProductSortBy: (value: ProductSort) => void;
  setSelectedVariantSku: (sku: string) => void;
  toggleCurrentProductSortDirection: () => void;
}

interface State {
  currentPage: number;
  currentProductSortBy: ProductSort;
  currentProductSortDirection: SortDirection;
  product: Product | null;
  productSearch: Product[] | null;
  productsMeta: Meta | null;
  selectedAttributes: Record<string, string>;
  selectedVariantSku: string | null;
}

export const useProductStore = defineStore<typeof STORE_ID, State, _GettersTree<State>, Actions>(STORE_ID, {
  actions: {
    async fetchProduct(slug: string, locale: Locale) {
      try {
        const response = await this.$api.fetch<Product>(ApiMethod.GET, `/products/${slug}`, {
          locale,
        });

        if (response.data) {
          this.product = response.data;
          this.selectedAttributes = {};
          this.selectedVariantSku = null;
          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);
      }

      return null;
    },
    async searchProducts(query: string, locale: Locale, quickSearch?: boolean) {
      try {
        const response = await this.$api.fetch<Product[]>(ApiMethod.GET, '/products', {
          keywords: query.replaceAll(' ', ','),
          limit: quickSearch ? DEFAULT_QUICK_SEARCH_PAGE_LIMIT : DEFAULT_PAGE_LIMIT,
          locale,
          page: quickSearch ? 1 : this.currentPage,
          sort_by: quickSearch ? undefined : this.currentProductSortBy,
          sort_dir: quickSearch ? undefined : this.currentProductSortDirection,
        });

        if (response.data) {
          if (!quickSearch) {
            this.productSearch = response.data;
            this.productsMeta = response.meta ?? null;
          }

          return response.data;
        }
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);
      }

      return null;
    },
    selectVariantAttribute(label: string, value: string) {
      if (this.selectedAttributes[label] === value) {
        delete this.selectedAttributes[label];
      } else {
        this.selectedAttributes[label] = value;
      }

      const variations = this.product?.attribute_variations;
      if (!variations) {
        this.selectedVariantSku = null;
        return;
      }

      const selectedEntries = Object.entries(this.selectedAttributes);
      if (!selectedEntries.length) {
        this.selectedVariantSku = null;
        return;
      }

      const skuSets = selectedEntries
        .map(([attrLabel, attrValue]) => {
          const group = variations.find((v) => v.label === attrLabel);
          const variation = group?.variations?.find((v) => v.value === attrValue);
          return variation?.skus ?? [];
        })
        .filter((skus) => skus.length > 0);

      if (!skuSets.length) {
        this.selectedVariantSku = null;
        return;
      }

      const intersection = skuSets.reduce((acc, skus) => acc.filter((sku) => skus.includes(sku)));
      this.selectedVariantSku = intersection[0] ?? null;
    },
    setCurrentProductSortBy(value: ProductSort) {
      this.currentPage = 1;
      this.currentProductSortBy = value;
    },
    setSelectedVariantSku(sku: string) {
      this.selectedVariantSku = sku;
    },
    toggleCurrentProductSortDirection() {
      this.currentPage = 1;
      this.currentProductSortDirection =
        this.currentProductSortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
    },
  },
  state: () => ({
    currentPage: 1,
    currentProductSortBy: ProductSort.NAME,
    currentProductSortDirection: SortDirection.ASC,
    product: null,
    productSearch: null,
    productsMeta: null,
    selectedAttributes: {},
    selectedVariantSku: null,
  }),
});
